import { useCallback, useEffect, useState, type FormEvent } from "react";
import { FileText, Upload, X } from "lucide-react";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ReviewSettingsFields } from "@/components/review/ReviewSettingsFields";
import { cn } from "@/lib/utils";
import { getReviewDisplayFileName } from "@/lib/review-display";
import { reviewToFormData } from "@/lib/review-form-data";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon";
import { GoogleDrivePicker, type SelectedDriveFile } from "@/components/wizard/GoogleDrivePicker";
import { getGoogleDriveStatus } from "@/lib/api/google-drive";
import { getEmailStatus } from "@/lib/api/email";
import { EmailPicker, type EmailImportPayload } from "@/components/wizard/EmailPicker";
import type { ReviewFormData, ReviewRecord, SupportingDocInput } from "@/types";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function isAllowedContractFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type === "application/pdf" ||
    file.type === DOCX_MIME ||
    name.endsWith(".pdf") ||
    name.endsWith(".docx")
  );
}

export interface NewVersionSheetProps {
  sourceReview: ReviewRecord;
  isOpen: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (
    file: File | null,
    driveFile: SelectedDriveFile | null,
    formData: ReviewFormData,
  ) => void;
}

export function NewVersionSheet({
  sourceReview,
  isOpen,
  isSubmitting = false,
  onClose,
  onSubmit,
}: NewVersionSheetProps) {
  const [formData, setFormData] = useState<ReviewFormData>(() => reviewToFormData(sourceReview));
  const [newSupportingDocs, setNewSupportingDocs] = useState<SupportingDocInput[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDriveFile, setSelectedDriveFile] = useState<SelectedDriveFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [driveAvailable, setDriveAvailable] = useState(false);
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setSelectedDriveFile(null);
      setIsDragging(false);
      setNewSupportingDocs([]);
      return;
    }
    setFormData(reviewToFormData(sourceReview));
  }, [isOpen, sourceReview]);

  useEffect(() => {
    let cancelled = false;

    async function loadDriveStatus() {
      try {
        const status = await getGoogleDriveStatus();
        if (!cancelled) {
          setDriveAvailable(status.connected && !status.reconnectRequired);
        }
      } catch {
        if (!cancelled) setDriveAvailable(false);
      }
    }

    if (isOpen) void loadDriveStatus();
    async function loadEmailStatus() {
      try {
        const status = await getEmailStatus();
        if (!cancelled) setEmailAvailable(status.microsoft.connected || status.imap.connected);
      } catch {
        if (!cancelled) setEmailAvailable(false);
      }
    }
    if (isOpen) void loadEmailStatus();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const hasSelection = selectedFile !== null || selectedDriveFile !== null;

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) onClose();
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && isAllowedContractFile(file)) {
      setSelectedDriveFile(null);
      setSelectedFile(file);
    }
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isAllowedContractFile(file)) {
      setSelectedDriveFile(null);
      setSelectedFile(file);
    }
  }, []);

  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setSelectedDriveFile(null);
  }, []);

  const handleEmailImport = useCallback((payload: EmailImportPayload) => {
    const threadFile = new File(
      [payload.threadText],
      `${payload.conversation.subject || "email-thread"}.txt`,
      { type: "text/plain" },
    );
    const importedDocs: SupportingDocInput[] = [
      {
        id: `email-thread-${Date.now()}`,
        file: threadFile,
        docType: "background",
      },
      ...payload.files
        .filter(({ attachment }) => attachment.id !== payload.contractFile?.attachment.id)
        .slice(0, 4)
        .map(({ file }, index) => ({
          id: `email-attachment-${Date.now()}-${index}`,
          file,
          docType: "background" as const,
        })),
    ];
    setNewSupportingDocs(importedDocs);
    if (payload.contractFile) {
      setSelectedDriveFile(null);
      setSelectedFile(payload.contractFile.file);
    }
    setEmailPickerOpen(false);
  }, []);

  const fullFileName = selectedFile?.name ?? selectedDriveFile?.name ?? "";
  const displayFileName = getReviewDisplayFileName(fullFileName || null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!hasSelection || isSubmitting) return;
    onSubmit(selectedFile, selectedDriveFile, {
      ...formData,
      supportingDocs: newSupportingDocs,
    });
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col sm:max-w-xl"
          allowOutsideInteraction={drivePickerOpen}
        >
          <SheetHeader>
            <SheetTitle>New version</SheetTitle>
            <SheetDescription className={pageDescriptionMobileHiddenClassName}>
              Upload an updated contract and adjust review settings. A new version will be created
              from the current review family.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <SheetBody className="flex flex-col gap-6">
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-200">Updated contract document</p>
                {!hasSelection ? (
                  <>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                      }}
                      onDrop={handleDrop}
                      className={cn(
                        "relative rounded-xl border-2 border-dashed p-8 transition-all",
                        isDragging
                          ? "border-violet-500 bg-violet-500/10"
                          : "border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] hover:border-violet-400/50 dark:border-slate-700 dark:bg-slate-900/50",
                      )}
                    >
                      <input
                        type="file"
                        accept=".pdf,.docx"
                        onChange={handleFileInput}
                        className="absolute inset-0 cursor-pointer opacity-0"
                        disabled={isSubmitting}
                      />
                      <div className="flex flex-col items-center text-center">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-violet-500/10 text-violet-500">
                          <Upload className="h-6 w-6" />
                        </div>
                        <p className="text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
                          Drop your updated PDF or Word document here
                        </p>
                        <p className="mt-1 text-xs text-[var(--lm-muted)] dark:text-slate-500">
                          Supports PDF and DOCX files
                        </p>
                      </div>
                    </div>

                    {driveAvailable ? (
                      <>
                        <div className="flex items-center gap-3">
                          <div className="h-px flex-1 bg-[var(--lm-border-subtle)] dark:bg-slate-800" />
                          <span className="text-xs uppercase tracking-wide text-[var(--lm-muted)]">
                            or
                          </span>
                          <div className="h-px flex-1 bg-[var(--lm-border-subtle)] dark:bg-slate-800" />
                        </div>
                        <button
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => setDrivePickerOpen(true)}
                          className="flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-4 py-3 text-sm font-medium transition-colors hover:border-violet-400/50 dark:border-slate-700 dark:bg-slate-900/50"
                        >
                          <GoogleDriveIcon className="h-5 w-5" />
                          Import from Google Drive
                        </button>
                      </>
                    ) : null}
                  </>
                ) : (
                  <div className="flex min-w-0 items-center gap-3 overflow-hidden rounded-xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] p-4 dark:border-slate-700 dark:bg-slate-900/50">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-500/10">
                      <FileText className="h-5 w-5 text-violet-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100"
                        title={fullFileName || undefined}
                      >
                        {displayFileName}
                      </p>
                      <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">Ready to analyze</p>
                    </div>
                    {!isSubmitting ? (
                      <button
                        type="button"
                        onClick={handleClear}
                        className="shrink-0 rounded-lg p-2 text-[var(--lm-muted)] hover:bg-[var(--lm-hover)] hover:text-[var(--lm-foreground-strong)] dark:hover:bg-slate-800"
                        aria-label="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                )}
              </div>

              <ReviewSettingsFields
                reviewId={sourceReview.id}
                formData={formData}
                onFormDataChange={setFormData}
                newSupportingDocs={newSupportingDocs}
                onNewSupportingDocsChange={setNewSupportingDocs}
                active={isOpen}
                onEmailClick={emailAvailable && !isSubmitting ? () => setEmailPickerOpen(true) : undefined}
              />
            </SheetBody>

            <SheetFooter className="border-t border-white/10">
              <Button type="button" variant="surface" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="accent" disabled={!hasSelection || isSubmitting}>
                {isSubmitting ? "Processing…" : "Create version"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <GoogleDrivePicker
        open={drivePickerOpen}
        onOpenChange={setDrivePickerOpen}
        onSelect={(file) => {
          setSelectedFile(null);
          setSelectedDriveFile(file);
          setDrivePickerOpen(false);
        }}
      />
      <EmailPicker
        open={emailPickerOpen}
        onOpenChange={setEmailPickerOpen}
        destination="background"
        onImport={handleEmailImport}
      />
    </>
  );
}
